<template>
  <v-main>
    <app-navigation />

    <v-sheet id="scrolling-techniques-7" class="ukpl-blue-card-container" height="100%">
      <v-container v-if="loading">
        <v-row class="fill-height" align-content="center" justify="center">
          <v-col class="text-subtitle-1 text-center" cols="12">
            Loading Roster
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-progress-linear color="blue" indeterminate rounded height="6"></v-progress-linear>
          </v-col>
        </v-row>
      </v-container>
      <v-container class="elevation-12" fluid height="100%" v-if="!loading">
        <v-row v-if="captainedTeams.length > 1" class="mb-4">
          <v-col cols="12">
            <v-select
              v-model="selectedTeamCombinedId"
              :items="captainedTeams"
              item-text="displayName"
              item-value="combinedId"
              label="Select Team Roster to Manage"
              outlined
              dense
              @change="onTeamChanged"
            ></v-select>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-card>
              <v-card-text>
                <v-icon color="red"> mdi-close-circle </v-icon>
                Available. NOT ON ROSTER</v-card-text>
            </v-card>
          </v-col>
          <v-col>
            <v-card>
              <v-card-text>
                <v-icon color="green"> mdi-check-circle </v-icon>

                Selected. ON ROSTER</v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-card class="mx-auto">
          <v-toolbar color="indigo" dark>
            <v-toolbar-title>Choose Roster (Tap players to select/unselect)</v-toolbar-title>
          </v-toolbar>
          <v-list>
            <v-list-item-group v-model="selectedRoster" active-class="blue--text" multiple>
              <template v-for="(item, index) in items">
                <v-list-item :key="'item-' + item.fullname" v-bind:disabled="locked || !item.paid || !isCaptainForSelectedTeam">
                  <template v-slot:default="{ active }">
                    <v-list-item-action>
                      <v-icon v-if="!active" color="red">
                        mdi-close-circle
                      </v-icon>

                      <v-icon v-else color="green"> mdi-check-circle </v-icon>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title v-if="item.paid">{{ item.fullname }}</v-list-item-title>
                      <v-list-item-title v-else>{{ item.fullname }} <strong style="color: red">Not Paid</strong></v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-avatar>
                      <v-img :src="calcPreviewImage(item.photo, item.photoType)" max-width="40"></v-img>
                    </v-list-item-avatar>
                  </template>
                </v-list-item>
                <v-divider :key="'div-' + index" v-if="index < items.length - 1"></v-divider>
              </template>
            </v-list-item-group>
          </v-list>
        </v-card>
        <v-card class="mx-auto">
          <v-toolbar color="indigo" dark>
            <v-toolbar-title>Guest Players</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              v-if="isCaptainForSelectedTeam && !locked && canAddGuest"
              icon
              @click="openGuestDialog"
              title="Add Guest Player"
            >
              <v-icon>mdi-plus-circle</v-icon>
            </v-btn>
          </v-toolbar>
          <v-list v-if="guests.length > 0">
            <template v-for="(guest, index) in guests">
              <v-list-item :key="'guest-' + index">
                <v-list-item-avatar>
                  <v-img :src="calcPreviewImage(guest.photo, guest.photoType)" max-width="40"></v-img>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ guest.fullname }}</v-list-item-title>
                  <v-list-item-subtitle>{{ guest.division }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="isCaptainForSelectedTeam && !locked">
                  <v-btn icon color="red" @click="removeGuest(index)" title="Remove Guest">
                    <v-icon>mdi-minus-circle</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider :key="'guest-div-' + index" v-if="index < guests.length - 1"></v-divider>
            </template>
          </v-list>
          <v-card-text v-if="guests.length === 0" class="text-center grey--text">
            No guest players added
          </v-card-text>
          <v-card-text v-if="selectedLeagueId === 1" class="caption grey--text pt-0">
            Max 2 guests for this league ({{ guests.length }}/2 used)
          </v-card-text>
          <v-card-actions v-if="isCaptainForSelectedTeam && !locked && canAddGuest">
            <v-btn text color="blue accent-3" @click="openGuestDialog">
              <v-icon left>mdi-plus</v-icon>
              Add Guest Player
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-dialog v-model="guestDialog" scrollable max-width="300px">
          <v-card>
            <v-card-title>
              <v-text-field class="mx-3" flat label="Search for player" prepend-inner-icon="mdi-search" solo-inverted
                v-model="guestSearch" clearable @click:clear="guestSearch = ''"></v-text-field>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-if="filteredGuestOptions.length === 0">No Players Available</v-card-text>
            <v-card-text>
              <v-list>
                <v-list-item-group active-class="blue--text">
                  <template v-for="(item, index) in filteredGuestOptions">
                    <v-list-item :key="'gopt-' + item.playerId" @click="addGuest(item)">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.fullname }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.division }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-avatar>
                        <v-img :src="calcPreviewImage(item.photo, item.photoType)" max-width="40"></v-img>
                      </v-list-item-avatar>
                    </v-list-item>
                    <v-divider :key="'gopt-div-' + index" v-if="index < filteredGuestOptions.length - 1"></v-divider>
                  </template>
                </v-list-item-group>
              </v-list>
            </v-card-text>
            <v-divider></v-divider>
          </v-card>
        </v-dialog>

        <v-card>
          <v-card-title v-if="isCaptainForSelectedTeam">
            <v-btn rounded color="blue accent-3" dark @click="lock" v-bind:disabled="locked">Lock Roster for this
              round</v-btn>
          </v-card-title>
        </v-card>
        <v-card v-if="isCaptainForSelectedTeam">
          <v-alert v-if="!locked" dense outlined type="error">
            You <strong>MUST</strong> lock you roster before the day you are
            playing.
          </v-alert>
          <v-alert v-if="locked" dense outlined type="error">
            <strong>Roster Locked</strong>
          </v-alert>
        </v-card>
        <v-card v-if="!isCaptainForSelectedTeam">
          <v-alert dense outlined type="info">
            You are viewing the team roster. Only the team captain can edit it.
          </v-alert>
        </v-card>
        <v-snackbar v-model="lockvalidation" centered :timeout="5000" rounded="pill" color="red">
          You must have at least 5 players selected (green tick) on your roster
          (including guests) to lock your roster.
        </v-snackbar>
      </v-container>
    </v-sheet>
    <!--    <v-snackbar :timeout="2000" v-model="messageon" color="red accent-4" rounded="pill" elevation="24">
            {{ message }}
          </v-snackbar>
          <v-snackbar :timeout="2000" v-model="savesuccess" color="green accent-4" rounded="pill" elevation="24">
            Details Saved.
          </v-snackbar>-->
  </v-main>
</template>

<script>

import rosterService from "../services/roster.service";
import userService from "../services/user.service";
import UtilService from "../services/util.service";
//import Roster from "../model/roster";

import AppNavigation from "../components/AppNavigation";

export default {
  name: "Roster",
  components: {
    AppNavigation,
  },
  data: () => ({
    bucket: "https://assets.ukpl.co.uk/",
    photobase: "images/",
    thumbbase: "thumbs/images/",
    selectedRoster: [],
    items: [],
    locked: false,
    rosterlock: null,
    guestDialog: false,
    guestSearch: '',
    guests: [],
    allGuestPlayers: [],
    loading: true,
  lockvalidation: false,
  guestLoading: false,
  captainedTeams: [],
  selectedTeamId: null,
  selectedLeagueId: null,
  selectedTeamCombinedId: null,
  }),
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    filteredGuestOptions() {
      const selectedIds = new Set(this.guests.map(g => g.playerId));
      return this.allGuestPlayers.filter(item => {
        if (selectedIds.has(item.playerId)) return false;
        return item.fullname.toLowerCase().includes((this.guestSearch || '').toLowerCase());
      });
    },
    canAddGuest() {
      if (this.selectedLeagueId === 1) {
        return this.guests.length < 2;
      }
      return true;
    },
    isCaptainForSelectedTeam() {
      if (!this.selectedTeamCombinedId || !this.captainedTeams) return false;
      const team = this.captainedTeams.find(t => t.combinedId === this.selectedTeamCombinedId);
      return team ? team.isCaptain : false;
    }
  },
  methods: {
    loadGuestPlayers() {
      this.guestLoading = true;
      rosterService.getAvailableGuests(this.selectedTeamId, this.selectedLeagueId).then(
        (response) => {
          this.allGuestPlayers = response.data;
          this.guestLoading = false;
        },
        (error) => {
          this.message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
          this.guestLoading = false;
        }
      );
    },
    onTeamChanged(newCombinedId) {
      if (newCombinedId) {
        this.selectedTeamId = parseInt(newCombinedId.split('-')[0]);
        this.selectedLeagueId = parseInt(newCombinedId.split('-')[1]);
      }
      // Reload roster data when team changes
      this.loading = true;
      this.getRoster();
    },
    openGuestDialog() {
      this.guestSearch = '';
      this.loadGuestPlayers();
      this.guestDialog = true;
    },
    addGuest(item) {
      this.guests.push(item);
      rosterService.updateGuestRoster(this.selectedTeamId, this.guests, this.selectedLeagueId);
      this.guestDialog = false;
      this.guestSearch = '';
    },
    removeGuest(index) {
      this.guests.splice(index, 1);
      rosterService.updateGuestRoster(this.selectedTeamId, this.guests, this.selectedLeagueId);
    },
    lock() {
      if (this.guests.length + this.selectedRoster < 4) {
        this.lockvalidation = true;
      } else {
        this.locked = true;
        rosterService.lockRoster(this.selectedTeamId, this.selectedLeagueId);
      }
    },

    getRoster() {
      this.locked = false;
      rosterService.getRoster(this.selectedTeamId, this.selectedLeagueId).then(
        (response) => {
          // Use array replacement for reactivity
          const nonGuestPlayers = [];
          const guestPlayers = [];
          response.data.players.forEach((player) => {
            if (!player.guest) {
              nonGuestPlayers.push(player);
            } else {
              guestPlayers.push(player);
            }
          });
          this.items = nonGuestPlayers;
          this.guests = guestPlayers;
          this.rosterlock = response.data.rosterLock;
          if (this.rosterlock != null) {
            this.locked = true;
          }
          // Use array replacement for selectedRoster
          this.selectedRoster = this.items.map((item, index) => item.selected ? index : null).filter(i => i !== null);
          this.loading = false;
        },
        (error) => {
            console.error('Error loading roster:', error);
            this.message =
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              error.message ||
              error.toString();
            this.loading = false;
        }
      );
        // Fallback: ensure loading spinner never hangs
        setTimeout(() => {
          if (this.loading) {
            console.warn('Roster loading timeout fallback triggered');
            this.loading = false;
          }
        }, 5000);
    },
    calcPreviewImage(photoId, photoType) {
      if (photoId != undefined && photoType != undefined) {
        let filename = this.bucket + this.thumbbase + photoId;
        if (photoType === "image/jpeg") {
          filename = filename + ".jpg";
        } else if (photoType === "image/png") {
          filename = filename + ".png";
        } else {
          filename = "";
        }
        return filename;
      } else {
        return "";
      }
    },

  },
  async created() {
    try {
      this.loading = true;
      const resp = await userService.getUserTeams();
      
      let leagues = [];
      try {
        const leaguesResp = await UtilService.getLeagues();
        leagues = leaguesResp.data || [];
      } catch (err) {
        console.error("Could not load leagues", err);
      }

      const rawTeams = resp.data || [];
      this.captainedTeams = rawTeams.map(team => {
        let leagueName = "Unknown League";
        if (team.leagueId) {
            const league = leagues.find(l => l.id === team.leagueId);
            if (league && league.leagueName) {
                leagueName = league.leagueName;
            }
        }
        return {
           ...team,
           displayName: `${team.name} (${leagueName})`,
           combinedId: `${team.id}-${team.leagueId || 0}`
        };
      });

      if (this.captainedTeams.length > 0) {
        this.selectedTeamCombinedId = this.captainedTeams[0].combinedId;
        this.selectedTeamId = this.captainedTeams[0].id;
        this.selectedLeagueId = this.captainedTeams[0].leagueId;
      }
      this.getRoster();
    } catch (e) {
      console.error(e);
      this.getRoster(); // fallback
    }
  },
  updated() { },
  mounted() { },
  watch: {
    selectedRoster() {
      if (!this.locked && !this.loading && this.isCaptainForSelectedTeam) { // wait until initial load is finished
        var currentRoster = [];
        this.selectedRoster.forEach((element) => {
          currentRoster.push(this.items[element]);
        });
        // post currentRoster to back end service, to replace current roster for this team.
        rosterService.updateRoster(this.selectedTeamId, currentRoster, this.selectedLeagueId);
      }
    },
      guestDialog(val) {
        if (val) {
          this.loadGuestPlayers();
        }
      },
  },
};
</script>
